import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { partnerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * /business/start — the dedicated B2B onboarding screen.
 *
 * The landing page (/business) stays pure marketing; every CTA lands here.
 * A three-step ladder (account → venue → plan) that always knows where the
 * visitor stands: logged out → auth buttons; logged in → venue form;
 * venue exists → onward to the dashboard, where the plan is picked.
 */

const StepBadge = ({ index, state, label }) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
        state === 'done'
          ? 'bg-emerald-500 text-white'
          : state === 'current'
            ? 'bg-white text-gray-950'
            : 'bg-white/10 text-white/40'
      }`}
    >
      {state === 'done' ? <Check size={13} /> : index}
    </div>
    <span
      className={`text-xs font-semibold ${
        state === 'current' ? 'text-white' : state === 'done' ? 'text-emerald-300' : 'text-white/30'
      }`}
    >
      {label}
    </span>
  </div>
);

const BusinessStartPage = () => {
  const { t } = useTranslation('business');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isRealUser = Boolean(user) && user?.authProvider !== 'guest';

  const [venueName, setVenueName] = useState('');
  const [website, setWebsite] = useState('');
  const [creating, setCreating] = useState(false);
  const [existing, setExisting] = useState(null);
  const [checkingVenue, setCheckingVenue] = useState(true);

  useEffect(() => {
    document.title = 'Okio for Business — Get started';
    return () => {
      document.title = 'Okio';
    };
  }, []);

  useEffect(() => {
    if (!isRealUser) {
      setCheckingVenue(false);
      return;
    }
    partnerAPI
      .getMine()
      .then((response) => setExisting(response.data.data.partner))
      .catch(() => {})
      .finally(() => setCheckingVenue(false));
  }, [isRealUser]);

  const create = async () => {
    if (venueName.trim().length < 2) {
      toast.error(t('start.nameRequired'));
      return;
    }
    setCreating(true);
    try {
      const response = await partnerAPI.createSelfServe({
        name: venueName.trim(),
        website: website.trim() || null,
      });
      const partner = response.data.data.partner;
      toast.success(t('start.created'));
      navigate(`/p/${partner.slug}/dashboard`);
    } catch (err) {
      console.error('[BusinessStart] Venue creation failed:', err);
      toast.error(err?.response?.data?.detail || t('start.createFailed'));
      setCreating(false);
    }
  };

  // Which rung of the ladder is the visitor on?
  const step = !isRealUser ? 1 : existing ? 3 : 2;

  const stepState = (index) => (index < step ? 'done' : index === step ? 'current' : 'todo');

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/business">
            <img src="/logo_white.webp" alt="OKIO" className="h-5 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {/* Identity anchor: the stepper below already *acts* on auth
                state, but a returning user needs to see it in the chrome —
                otherwise "will I have to register again?" lingers */}
            {isRealUser && (
              <>
                <span className="text-xs text-emerald-400/80">
                  {t('startPage.signedInAs', {
                    name: user?.firstName || user?.username || user?.email,
                  })}
                </span>
                <button
                  onClick={logout}
                  className="text-xs font-medium text-white/40 hover:text-white transition-colors"
                >
                  {t('header.signOut')}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/business"
            className="inline-flex items-center gap-1.5 text-white/40 hover:text-white/70 text-xs mb-6 transition-colors"
          >
            <ArrowLeft size={13} />
            {t('startPage.back')}
          </Link>

          <div className="rounded-3xl bg-gray-900 border border-white/10 p-8">
            {/* Stepper */}
            <div className="flex items-center justify-between mb-8">
              <StepBadge index={1} state={stepState(1)} label={t('startPage.stepAccount')} />
              <div className="flex-1 h-px bg-white/10 mx-3" />
              <StepBadge index={2} state={stepState(2)} label={t('startPage.stepVenue')} />
              <div className="flex-1 h-px bg-white/10 mx-3" />
              <StepBadge index={3} state={stepState(3)} label={t('startPage.stepPlan')} />
            </div>

            {checkingVenue ? (
              <div className="flex justify-center py-10">
                <Loader2 size={22} className="animate-spin text-emerald-400" />
              </div>
            ) : step === 1 ? (
              <>
                <h1 className="text-xl font-bold mb-2">{t('startPage.accountTitle')}</h1>
                <p className="text-white/45 text-sm mb-7 leading-relaxed">
                  {t('startPage.accountSubtitle')}
                </p>
                <div className="space-y-3">
                  <Link
                    to="/register"
                    state={{ from: { pathname: '/business/start' } }}
                    className="w-full px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {t('start.register')}
                    <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/login"
                    state={{ from: { pathname: '/business/start' } }}
                    className="w-full px-8 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors inline-flex items-center justify-center"
                  >
                    {t('start.login')}
                  </Link>
                </div>
              </>
            ) : step === 2 ? (
              <>
                <h1 className="text-xl font-bold mb-2">{t('startPage.venueTitle')}</h1>
                <p className="text-white/45 text-sm mb-7 leading-relaxed">
                  {t('startPage.venueSubtitle')}
                </p>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={venueName}
                    onChange={(event) => setVenueName(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && create()}
                    placeholder={t('start.venueNamePlaceholder')}
                    maxLength={80}
                    autoFocus
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400/50 text-white text-sm placeholder-white/30 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && create()}
                    placeholder={t('start.websitePlaceholder')}
                    maxLength={200}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400/50 text-white text-sm placeholder-white/30 focus:outline-none"
                  />
                  <button
                    onClick={create}
                    disabled={creating}
                    className="w-full px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {creating ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <ArrowRight size={16} />
                    )}
                    {t('start.createVenue')}
                  </button>
                  <p className="text-white/30 text-xs text-center pt-1">{t('start.freeNote')}</p>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold mb-2">{t('startPage.doneTitle')}</h1>
                <p className="text-white/45 text-sm mb-7 leading-relaxed">
                  {t('startPage.doneSubtitle', { name: existing?.name })}
                </p>
                <Link
                  to={`/p/${existing?.slug}/dashboard`}
                  className="w-full px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors inline-flex items-center justify-center gap-2"
                >
                  {t('start.toDashboard')}
                  <ArrowRight size={16} />
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BusinessStartPage;
